import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { useClassCode, type ClassPass } from '../context/ClassCodeContext';
import { gameServer } from '../game/client';
import type { ClassBoard } from './classes';

// The section on show, out of the ones this device has a pass for. Its board comes from the game server and is
// kept on the device, so the schedule still opens on the train with no signal.

const KEY = 'gs.class';
const cacheKey = (section: string) => `gs.board.${section}`;

function cached(section: string): ClassBoard | null {
  try {
    return JSON.parse(localStorage.getItem(cacheKey(section)) || 'null');
  } catch {
    return null;
  }
}

// the section on show (the one this device last picked), also for pages outside the site's frame, like the slime's room
export function pick(passes: ClassPass[]): string {
  try {
    const v = localStorage.getItem(KEY);
    if (v && passes.some((p) => p.section === v)) return v;
  } catch {
    /* fall through */
  }
  return passes[0]?.section ?? '';
}

interface Ctx {
  board: ClassBoard | null;
  status: 'ready' | 'loading' | 'offline';
  sections: ClassPass[];
  setClass: (section: string) => void;
}
const ClassCtx = createContext<Ctx | null>(null);

export function ClassProvider({ children }: { children: ReactNode }) {
  const { passes, forget } = useClassCode();
  const [section, setSection] = useState(() => pick(passes));
  const [board, setBoard] = useState<ClassBoard | null>(() => cached(section));
  const [status, setStatus] = useState<Ctx['status']>('loading');
  // the teacher edits boards on the teacher side, so a tab left open asks again when the student comes back to it
  const [back, setBack] = useState(0);
  const loadedFor = useRef<string | null>(null);
  useEffect(() => {
    let last = Date.now();
    const seen = () => {
      if (document.visibilityState !== 'visible' || Date.now() - last < 60_000) return;
      last = Date.now();
      setBack((n) => n + 1);
    };
    document.addEventListener('visibilitychange', seen);
    return () => document.removeEventListener('visibilitychange', seen);
  }, []);

  // a pass that was just added or removed changes which section is on show
  useEffect(() => {
    if (!passes.some((p) => p.section === section)) setSection(pick(passes));
  }, [passes, section]);

  useEffect(() => {
    const pass = passes.find((p) => p.section === section);
    if (!pass) return;
    let live = true;
    // a new section shows its own copy while it loads; coming back to the tab keeps the board on show
    if (loadedFor.current !== section) {
      loadedFor.current = section;
      setBoard(cached(section));
      setStatus('loading');
    }
    (async () => {
      const base = await gameServer();
      if (!base) return live && setStatus('offline');
      try {
        const r = await fetch(`${base}/board`, { headers: { Authorization: `Bearer ${pass.pass}` } });
        if (r.status === 401) {
          // the pass has run out (new school year) or the class was removed: ask for a code again
          localStorage.removeItem(cacheKey(section));
          forget(section);
          return;
        }
        if (!r.ok) throw new Error(String(r.status));
        const b = (await r.json()) as ClassBoard;
        if (!live) return;
        setBoard(b);
        setStatus('ready');
        localStorage.setItem(cacheKey(section), JSON.stringify(b));
      } catch {
        if (live) setStatus('offline');
      }
    })();
    return () => {
      live = false;
    };
    // passes changes on every unlock; only refetch when the pass for this section changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [section, passes.find((p) => p.section === section)?.pass, back]);

  const setClass = (next: string) => {
    setSection(next);
    try {
      localStorage.setItem(KEY, next);
    } catch {
      /* remembered for this visit only */
    }
  };

  return <ClassCtx.Provider value={{ board, status, sections: passes, setClass }}>{children}</ClassCtx.Provider>;
}

// The teacher side's Student view (src/game/BoardEditor.tsx): a board drawn the way its class sees it, with no pass.
export function BoardPreview({ board, children }: { board: ClassBoard; children: ReactNode }) {
  return <ClassCtx.Provider value={{ board, status: 'ready', sections: [], setClass: () => {} }}>{children}</ClassCtx.Provider>;
}

export function useClass() {
  const v = useContext(ClassCtx);
  if (!v) throw new Error('useClass outside ClassProvider');
  return v;
}

// For pages that only render once a board is on show (the shell waits for it)
export function useClassBoard() {
  const { board, setClass } = useClass();
  if (!board) throw new Error('no board yet');
  return { board, setClass };
}
